import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { fmtArr, fmtUsd0 } from "../format";

export interface NewBusinessData {
  /** Headline: new + PLG signed-contract ARR for the period. */
  totalArr: number;
  totalArrPctChange: number | null;
  priorLabel: string;
  newContracts: number;
  newArr: number;
  plgContracts: number;
  plgArr: number;
  poContracts: number;
  poArr: number;
  avgDealArr: number | null;
  firstBilledArr: number;
  firstBilledCustomers: number;
  firstBilledArrPctChange: number | null;
  source?: string;
}

function PctChip({ pct }: { pct: number | null }) {
  if (pct == null) return null;
  const up = pct >= 0;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
        up ? "bg-success-light text-success" : "bg-danger-light text-danger",
      )}
    >
      {up ? "↑" : "↓"} {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

export function NewBusinessCard({
  periodLabel,
  data,
}: {
  periodLabel: string;
  data: NewBusinessData | null;
}) {
  return (
    <Card className="relative h-full overflow-hidden p-5">
      <div className="absolute left-0 top-0 h-full w-1 bg-success" />
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        New — {periodLabel}
      </div>

      <div className="mt-2 flex items-center gap-2">
        <div className="text-3xl font-extrabold tracking-tight tabular-nums text-success">
          {data ? fmtArr(data.totalArr, 0) : "—"}
        </div>
        {data && <PctChip pct={data.totalArrPctChange} />}
      </div>
      <div className="text-[10px] text-muted-foreground">
        new + PLG contract ARR{data ? ` · vs ${data.priorLabel}` : ""}
      </div>

      <div className="mt-3 flex flex-col gap-1.5">
        <Row
          label={`New contracts (${data?.newContracts ?? "—"})`}
          value={data ? fmtUsd0(data.newArr) : null}
        />
        <Row
          label={`PLG contracts (${data?.plgContracts ?? "—"})`}
          value={data ? fmtUsd0(data.plgArr) : null}
        />
        <Row
          label={`Purchase orders (${data?.poContracts ?? "—"})`}
          value={data ? fmtUsd0(data.poArr) : null}
        />
        <Row label="Avg deal size" value={data ? fmtUsd0(data.avgDealArr) : null} />
        <div className="mt-1 border-t border-border pt-2">
          <div className="flex items-baseline justify-between text-xs">
            <span className="text-muted-foreground">Billed 1st time</span>
            <span className="flex items-center gap-1.5">
              <span className="font-semibold tabular-nums">
                {data ? fmtUsd0(data.firstBilledArr) : "—"}
              </span>
              {data && <PctChip pct={data.firstBilledArrPctChange} />}
            </span>
          </div>
          <div className="mt-0.5 text-[10px] text-muted-foreground">
            {data ? `${data.firstBilledCustomers} customers billed for the first time` : "—"}
          </div>
        </div>
      </div>

      <div className="mt-3 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/50">
        {data?.source ?? "BILLING_CONTRACTS (New + PLG) · REVENUE_WATERFALL first-billed"}
      </div>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-baseline justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums">{value ?? "—"}</span>
    </div>
  );
}
